import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ChartTooltip from "./ChartTooltip";
import ChartSkeleton from "./ChartSkeleton";
import { getNiceScale } from "../../../utils/index";

const BarChart = ({ data, selectedTab, activeTimeFilter, loading = false, csatAgentFilter, agentNames, singleAgentColor = null, nameToColor, is12MonthView = false,
  csatMonthPage = 0, hasDataInOtherPage = false, }) => {
  const [tooltip, setTooltip] = useState(null);


  // ✅ NEW: Get dynamic agent keys from data
  const getAgentKeys = () => {
    if (data && data.length > 0) {
      const keys = Object.keys(data[0]).filter(key => key !== 'day' && key.startsWith('agent'));
      return keys.sort(); // Sort to maintain consistent order
    }
    return ['agent1', 'agent2', 'agent3', 'agent4', 'agent5'];
  };

  // ✅ NEW: Map agent keys to display names
  const getAgentDisplayName = (agentKey) => {
    if (!agentNames || agentNames.length === 0) {
      // ✅ Don't show fallback names, return empty or the key itself
      return ''; // This will hide the agent if no real names exist
    }
    const index = parseInt(agentKey.replace('agent', '')) - 1;
    return agentNames[index] || '';
  };

  // Get agent color for single agent view (fallback, but singleAgentColor preferred)
  const getAgentColor = (agentName) => {
    return nameToColor[agentName.toLowerCase()] || "#148DFD";
  };

  const agentKeys = getAgentKeys();

  // ✅ MOVED UP: Filter agent keys for rendering bars (skip unnamed agents)
  const visibleAgentKeys = agentKeys.filter(key => getAgentDisplayName(key).trim() !== '');

  const getMaxValue = () => {
    if (selectedTab === "Agents Chats" || selectedTab === "Agents Tickets") {
      const totals = data.map((d) => {
        // ✅ UPDATED: Sum visible agent keys only
        return visibleAgentKeys.reduce((sum, key) => sum + (Number(d[key]) || 0), 0);
      });
      const max = Math.max(...(totals.length ? totals : [0]));
      return max <= 0 ? 1 : max;
    }
    if (selectedTab === "Agent CSAT" && csatAgentFilter !== 'All Agents') {
      const values = data.map((d) => Number(d.value) || 0);
      const max = Math.max(...(values.length ? values : [0]));
      return max <= 0 ? 100 : Math.max(max, 100); // Ensure scale at least to 100 for %
    }
    if (selectedTab === "Agent CSAT" && csatAgentFilter === 'All Agents') {
      const totals = data.map((d) => {
        return visibleAgentKeys.reduce((sum, key) => sum + (Number(d[key]) || 0), 0);
      });
      const max = Math.max(...(totals.length ? totals : [0]));
      return max <= 0 ? 100 : max; // For stacked %, scale to max sum
    }
    if (
      selectedTab === "Total Tickets" ||
      selectedTab === "Chat Satisfaction" ||
      selectedTab === "Chats handled vs missed" ||
      selectedTab === "New Tickets handled vs solved"
    ) {
      const totals = data.map((d) => {
        const p = Number(d?.newTickets || d?.primary) || 0;
        const s = Number(d?.solvedTickets || d?.secondary) || 0;
        return p + s;
      });
      const max = Math.max(...(totals.length ? totals : [0]));
      return max <= 0 ? 1 : max;
    }
    // ✅ UPDATED: For single-bar tabs like Total Chats/Website Traffic, ensure min scale 1
    const values = data.map((d) => Number(d?.value) || 0);
    const max = Math.max(...(values.length ? values : [0]));
    return max <= 0 ? 1 : max;
  };

  const chartHeight = 320; // Increased height to match Figma design with 80px gaps
  const maxValue = getMaxValue();
  const { max: yMax, ticks: yTicks } = getNiceScale(maxValue, 4);


  const hasData = (() => {
    // No data array at all → definitely no data (hide everything)
    if (!data || data.length === 0) return false;

    // ✅ UPDATED: For Agent CSAT, check for positive values OR presence (even 0/null) – FIXED for agent presence
    if (selectedTab === "Agent CSAT") {
      if (csatAgentFilter === 'All Agents') {
        // Check if any day has any agent present (key !== null, even if 0)
        return data.some(item => visibleAgentKeys.some(key => item[key] !== null));
      } else {
        // Single agent: Always show structure if periods exist
        return data.length > 0;
      }
    }

    // Check if at least one day has a positive value (unchanged for other tabs)
    return data.some(item => {
      // Agents Chats / Tickets → check visible agents only
      if (selectedTab === "Agents Chats" || selectedTab === "Agents Tickets") {
        return visibleAgentKeys.some(key => Number(item[key]) > 0);
      }

      // New Tickets handled vs solved
      if (selectedTab === "New Tickets handled vs solved") {
        return (Number(item.newTickets) || 0) > 0 || (Number(item.solvedTickets) || 0) > 0;
      }

      // Chats handled vs missed
      if (selectedTab === "Chats handled vs missed") {
        return (Number(item.primary) || 0) > 0 || (Number(item.secondary) || 0) > 0;
      }

      // Total Tickets / Chat Satisfaction
      if (selectedTab === "Total Tickets" || selectedTab === "Chat Satisfaction") {
        return (Number(item.primary || item.value) || 0) > 0 || (Number(item.secondary) || 0) > 0;
      }

      // Default tabs (Total Chats, Website Traffic, etc.)
      return Number(item.value || 0) > 0;
    });
  })();

  // ✅ NEW: Effective has data for y-axis visibility in paginated CSAT
  const effectiveHasData = hasData || (is12MonthView && selectedTab === "Agent CSAT" && hasDataInOtherPage);

  // ✅ NEW: Bar items for rendering empty structure in paginated CSAT – FIXED dummy for single agent
  let barItems = data;
  if (selectedTab === "Agent CSAT" && is12MonthView && effectiveHasData && (!data || data.length === 0)) {
    const dummyItem = csatAgentFilter === 'All Agents'
      ? agentKeys.reduce((acc, key) => ({ ...acc, [key]: null }), { day: '' })
      : { day: '', value: 0, hasAgentData: false };  // ✅ UPDATED: Set false for dummies to avoid showing thin bar
    barItems = Array.from({ length: 6 }, () => ({ ...dummyItem }));
  }

  const handleBarHover = (event, item, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isLast = index === barItems.length - 1;
    const isSecondLast = index === barItems.length - 2;
    const isThirdLastWhen30d = activeTimeFilter === "30 days" && index === barItems.length - 3;

    setTooltip({
      position: {
        x: isLast || isSecondLast || isThirdLastWhen30d ? rect.left - 90 : rect.left + 120,
        y: rect.top + rect.height / 2,
      },
      data: item,
      index: index,
    });
  };

  const handleBarLeave = () => {
    setTooltip(null);
  };

  const getTooltipData = (item, index) => {
    switch (selectedTab) {
      case "Agents Chats":
        // ✅ UPDATED: Dynamic agent stats
        const chatsStats = agentKeys.map(key => ({
          label: getAgentDisplayName(key),
          value: Number(item[key] || 0)
        })).filter(stat => stat.label.trim() !== '');
        if (chatsStats.length === 0) {
          return {
            title: "Total Chats",
            stats: [{ label: "Chats", value: 0 }],
          };
        }
        return {
          title: "Total Chats",
          stats: chatsStats,
        };
      case "Agents Tickets":
        // ✅ UPDATED: Dynamic agent stats
        const ticketsStats = agentKeys.map(key => ({
          label: getAgentDisplayName(key),
          value: Number(item[key] || 0)
        })).filter(stat => stat.label.trim() !== '');
        if (ticketsStats.length === 0) {
          return {
            title: "Total Tickets",
            stats: [{ label: "Tickets", value: 0 }],
          };
        }
        return {
          title: "Total Tickets",
          stats: ticketsStats,
        };
      case "New Tickets handled vs solved":
        return {
          title: "Tickets",
          stats: [
            { label: "New Tickets", value: Number(item.newTickets || 0) },
            { label: "Solved Tickets", value: Number(item.solvedTickets || 0) },
          ],
        };
      case "Agent CSAT":
        if (csatAgentFilter === "All Agents") {
          if (visibleAgentKeys.length === 0) {
            return {
              title: "All Agents",
              stats: [{ label: "CSAT Score", value: "0%" }],
            };
          }
          return {
            title: "All Agents",
            stats: agentKeys
              .map((key) => ({
                label: getAgentDisplayName(key),
                value: `${Number(item[key] || 0)}%`,
              }))
              .filter((stat) => stat.label.trim() !== ""),
          };
        } else {
          const csatValue = Number(item.value || 0);
          return {
            title: `${csatAgentFilter}`,
            stats: [{ label: "CSAT Score", value: `${csatValue}%` }],
          };
        }
      case "Total Chats":
        return {
          title: "Chats",
          stats: [
            { label: "Total Chats", value: Number(item.value || 0) },
            {
              label: "Average CSAT",
              value: `${Number(item.avgCsat ?? item.avg_csat ?? 0)}%`,
            },
          ],
        };
      case "Total Tickets":
        return {
          title: "Tickets",
          stats: [
            { label: "New Tickets", value: Number(item.primary || item.value || 0) },
            { label: "Resolved Tickets", value: Number(item.secondary || 0) },
          ],
        };
      case "Website Traffic":
        return {
          title: "Website Traffic",
          stats: [
            { label: "Total Visitors", value: Number(item.value || 0) },
            { label: "New Visitors", value: item.newVisitors ?? Math.floor((Number(item.value || 0)) * 0.7) },
          ],
        };
      case "Chat Satisfaction":
        return {
          title: "Chat Satisfaction",
          stats: [
            { label: "Total Chats", value: Number(item.totalChats || (Number(item.primary || 0) + Number(item.secondary || 0))) },
            { label: "Positive", value: Number(item.primary || 0) },
            { label: "Negative", value: Number(item.secondary || 0) },
            { label: "CSAT Score", value: `${Number(item.csat || 0)}%` },
          ],
        };
      case "Chats handled vs missed":
        return {
          title: "Chats",
          stats: [
            { label: "Handled", value: Number(item.primary || 0) },
            { label: "Missed", value: Number(item.secondary || 0) },
          ],
        };
      default:
        return {
          title: selectedTab,
          stats: [{ label: "Value", value: Number(item.value || 0) }],
        };
    }
  };

  const getXAxisLabels = () => {
    if (selectedTab === "Agent CSAT" && is12MonthView) {
      const shortMonths = csatMonthPage === 0
        ? ["Jan", "Feb", "Mar", "Apr", "May", "Jun"]
        : ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      if (data && data.length > 0) {
        return data.map((item, i) => {
          const original = item.day || '';
          const clean = original.trim();
          // If API already gives short month (e.g., "Jan"), keep it
          if (/^[A-Za-z]{3}$/.test(clean)) return clean;
          return shortMonths[i];
        });
      } else {
        return shortMonths;
      }
    }

    if (!data || data.length === 0) return [];

    return data.map(item => item.day || '');
  };

  const xAxisLabels = getXAxisLabels();

  if (loading) {
    return (
      <div style={{ margin: '10px' }}>
        <ChartSkeleton height={chartHeight} />
      </div>
    )
  }

  const displayTicks = yTicks.slice(0, 4);
  const yLabelOffset = 24;
  const barAreaHeight = 280; // Use most of the 320px height for bars
  const yLabelPositions = displayTicks.map(
    (t) => (chartHeight - 40) - (t / yMax) * barAreaHeight - yLabelOffset
  );

  // ✅ UPDATED: Separate logic for showLegend - for single CSAT, show if data exists (even all 0)
  const baseDataExists = data && data.length > 0;
  const showLegendForSingleCSAT = baseDataExists || (is12MonthView && selectedTab === "Agent CSAT" && hasDataInOtherPage);
  const showLegendForAllCSAT = (baseDataExists && visibleAgentKeys.length > 0) || (is12MonthView && selectedTab === "Agent CSAT" && hasDataInOtherPage && visibleAgentKeys.length > 0);
  const showLegend = (selectedTab === "Chats handled vs missed" ||
    selectedTab === "New Tickets handled vs solved" ||
    (selectedTab === "Agents Chats" || selectedTab === "Agents Tickets")) ||
    (selectedTab === "Agent CSAT" &&
      (csatAgentFilter === 'All Agents' ? showLegendForAllCSAT : showLegendForSingleCSAT)
    );  // ✅ FIXED: Show legend for single CSAT if data.length > 0 (even if all 0)

  // Update getLegendItems() for All Agents filtering:
  const getLegendItems = () => {
    if (selectedTab === "Chats handled vs missed") {
      return [
        { color: "#148DFD", label: "Handled" },
        { color: "#FF4D4F", label: "Missed" },
      ];
    } else if (selectedTab === "New Tickets handled vs solved") {
      return [
        { color: "#148DFD", label: "New Tickets" },
        { color: "#52C41A", label: "Solved Tickets" },
      ];
    } else if (
      selectedTab === "Agents Chats" ||
      selectedTab === "Agents Tickets" ||
      (selectedTab === "Agent CSAT" && csatAgentFilter === "All Agents")
    ) {
      // ✅ FIXED: For chats/tickets: active agents ( >0 somewhere); for CSAT All: all visible
      const agentsForLegend = (selectedTab === "Agents Chats" || selectedTab === "Agents Tickets")
        ? agentKeys.filter(key => data.some(item => Number(item[key]) > 0))
        : visibleAgentKeys;
      const items = agentsForLegend.map((key) => {
        const label = getAgentDisplayName(key);
        const color = nameToColor[label.toLowerCase()] || "#148DFD";
        return { color, label };
      });
      return items.filter((i) => i.label.trim() !== "");
    }
    if (selectedTab === "Agent CSAT" && csatAgentFilter !== "All Agents") {
      // ✅ FIXED: For single agent, show legend if data exists (remove hasData check)
      if (!showLegendForSingleCSAT) return [];
      // Use passed singleAgentColor for consistent color
      return [
        {
          color: singleAgentColor || "#148DFD",
          label: csatAgentFilter,
        },
      ];
    }
    return [];
  };
  const legendItems = getLegendItems();

  const isCustom = activeTimeFilter === 'custom';
  const isCustomCSAT = isCustom && selectedTab === "Agent CSAT";
  const barWidth = 32; // Fixed bar width
  const groupWidth = isCustomCSAT ? 56 : barWidth;
  const customGap = (() => {
    const n = barItems?.length || 0;
    if (n <= 3) return 64;
    if (n <= 6) return 40;
    return 24;
  })();

  // Custom ranges used fixed-width columns + gaps, which overflows the viewport when
  // there are many days (e.g. 29). Preset ranges use flex:1 — match that for long custom ranges.
  const CUSTOM_FIXED_LAYOUT_MAX_BARS = 16;
  const useFixedCustomBarLayout =
    isCustom &&
    (barItems?.length || 0) > 0 &&
    (barItems?.length || 0) <= CUSTOM_FIXED_LAYOUT_MAX_BARS;

  const leftPad = (() => {
    if (!effectiveHasData) return 20;
    // Keep Y labels flush with the title, but only reserve enough width for
    // the tick text + a tight gap before the grid/bars.
    const widest = displayTicks.reduce(
      (max, tick) => Math.max(max, String(tick).length),
      1
    );
    const tickTextWidth = widest * 7.5; // ~12px Inter digits
    const gapBeforeGrid = 8;
    return Math.ceil(tickTextWidth + gapBeforeGrid);
  })();
  const gridLeft = effectiveHasData ? leftPad : 0;

  // ✅ UPDATED: Empty bar style - now uses agent color for single CSAT (thin colored bar for 0 values)
  const getEmptyBarStyle = (useAgentColor = false, agentColor = "#F7F8F9") => ({
    width: "100%",
    height: "100%",  // Fills container (4px when no data)
    background: useAgentColor ? agentColor : "#F7F8F9",  // Agent color if flagged, else gray
    flexShrink: 0,
    cursor: "default",
    pointerEvents: "none",
    transition: "opacity 0.2s ease-in-out",
  });

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: "100%", minWidth: 0 }}>
      {/* Grid lines — start after Y-axis labels (aligned with plot / bars) */}
      <Box
        sx={{
          position: "absolute",
          left: gridLeft,
          right: 0,
          top: 0,
          height: chartHeight,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        {Array.from({ length: 3 }, (_, i) => {
          const tickValue = displayTicks[i + 1]; // Get the corresponding tick value
          const gridLineTop = (chartHeight - 40) - (tickValue / yMax) * barAreaHeight;
          return (
            <Box
              key={i}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${gridLineTop}px`,
                borderTop: "1px dashed #E5E9EC",
              }}
            />
          );
        })}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${chartHeight - 40}px`,
            borderTop: "1px solid #E5E9EC",
          }}
        />
      </Box>

      {/* Y-axis labels — left-aligned with "All Stats" title */}
      {effectiveHasData && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: leftPad,
            height: chartHeight,
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          {displayTicks.map((label, idx) => (
            <Typography
              key={idx}
              sx={{
                position: "absolute",
                top: `${yLabelPositions[idx]}px`,
                left: 0,
                fontSize: "12px",
                lineHeight: "12px",
                color: "#405261",
                fontFamily: "Inter",
                textAlign: "left",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>
      )}

      {/* Bars */}
      <Box
        sx={{
          paddingLeft: `${leftPad}px`,
          paddingRight: useFixedCustomBarLayout ? `${leftPad}px` : 0,
          height: chartHeight - 40,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: useFixedCustomBarLayout ? "center" : "space-between",
          columnGap: useFixedCustomBarLayout ? `${customGap}px` : undefined,
          position: "relative",
          zIndex: 1,
        }}
      >
        {barItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              flex: useFixedCustomBarLayout ? `0 0 ${groupWidth}px` : "1",
              width: useFixedCustomBarLayout ? `${groupWidth}px` : "auto",
              minWidth: useFixedCustomBarLayout ? undefined : 0,
            }}
          >
            {selectedTab === "Total Tickets" ? (
              <Box
                onMouseEnter={(e) => handleBarHover(e, item, index)}
                onMouseLeave={handleBarLeave}
                sx={{
                  width: `${barWidth}px`,
                  height: `${(((Number(item.primary) || 0) + (Number(item.secondary) || 0)) <= 0)
                    ? 4  // ✅ Thin height for no data
                    : (((Number(item.primary) || 0) + (Number(item.secondary) || 0)) / yMax) * barAreaHeight
                    }px`,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              >
                {((Number(item.primary) || 0) + (Number(item.secondary) || 0)) <= 0 ? (
                  <Box sx={getEmptyBarStyle()} />
                ) : (
                  <>
                    <Box
                      sx={{
                        backgroundColor: "#148DFD",
                        height: `${((Number(item.primary) || 0) /
                          Math.max(1, (Number(item.primary) || 0) + (Number(item.secondary) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                    <Box
                      sx={{
                        backgroundColor: "#52C41A",
                        height: `${((Number(item.secondary) || 0) /
                          Math.max(1, (Number(item.primary) || 0) + (Number(item.secondary) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                  </>
                )}
              </Box>
            ) : selectedTab === "Chat Satisfaction" ? (
              <Box
                onMouseEnter={(e) => handleBarHover(e, item, index)}
                onMouseLeave={handleBarLeave}
                sx={{
                  width: `${barWidth}px`,
                  height: `${(((Number(item.primary) || 0) + (Number(item.secondary) || 0)) <= 0)
                    ? 4  // ✅ Thin height for no data
                    : (((Number(item.primary) || 0) + (Number(item.secondary) || 0)) / yMax) * barAreaHeight
                    }px`,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              >
                {((Number(item.primary) || 0) + (Number(item.secondary) || 0)) <= 0 ? (
                  <Box sx={getEmptyBarStyle()} />
                ) : (
                  <>
                    <Box
                      sx={{
                        backgroundColor: "#52C41A",
                        height: `${((Number(item.primary) || 0) /
                          Math.max(1, (Number(item.primary) || 0) + (Number(item.secondary) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                    <Box
                      sx={{
                        backgroundColor: "#FF4D4F",
                        height: `${((Number(item.secondary) || 0) /
                          Math.max(1, (Number(item.primary) || 0) + (Number(item.secondary) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                  </>
                )}
              </Box>
            ) : selectedTab === "Chats handled vs missed" ? (
              <Box
                onMouseEnter={(e) => handleBarHover(e, item, index)}
                onMouseLeave={handleBarLeave}
                sx={{
                  width: `${barWidth}px`,
                  height: `${(((Number(item.primary) || 0) + (Number(item.secondary) || 0)) <= 0)
                    ? 4  // ✅ Thin height for no data
                    : (((Number(item.primary) || 0) + (Number(item.secondary) || 0)) / yMax) * barAreaHeight
                    }px`,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              >
                {((Number(item.primary) || 0) + (Number(item.secondary) || 0)) <= 0 ? (
                  <Box sx={getEmptyBarStyle()} />
                ) : (
                  <>
                    <Box
                      sx={{
                        backgroundColor: "#148DFD",
                        height: `${((Number(item.primary) || 0) /
                          Math.max(1, (Number(item.primary) || 0) + (Number(item.secondary) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                    <Box
                      sx={{
                        backgroundColor: "#FF4D4F",
                        height: `${((Number(item.secondary) || 0) /
                          Math.max(1, (Number(item.primary) || 0) + (Number(item.secondary) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                  </>
                )}
              </Box>
            ) : selectedTab === "New Tickets handled vs solved" ? (
              <Box
                onMouseEnter={(e) => handleBarHover(e, item, index)}
                onMouseLeave={handleBarLeave}
                sx={{
                  width: `${barWidth}px`,
                  height: `${(((Number(item.newTickets) || 0) + (Number(item.solvedTickets) || 0)) <= 0)
                    ? 4  // ✅ Thin height for no data
                    : (((Number(item.newTickets) || 0) + (Number(item.solvedTickets) || 0)) / yMax) * barAreaHeight
                    }px`,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              >
                {((Number(item.newTickets) || 0) + (Number(item.solvedTickets) || 0)) <= 0 ? (
                  <Box sx={getEmptyBarStyle()} />
                ) : (
                  <>
                    <Box
                      sx={{
                        backgroundColor: "#148DFD",
                        height: `${((Number(item.newTickets) || 0) /
                          Math.max(1, (Number(item.newTickets) || 0) + (Number(item.solvedTickets) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                    <Box
                      sx={{
                        backgroundColor: "#52C41A",
                        height: `${((Number(item.solvedTickets) || 0) /
                          Math.max(1, (Number(item.newTickets) || 0) + (Number(item.solvedTickets) || 0))) *
                          100
                          }%`,
                        width: "100%",
                      }}
                    />
                  </>
                )}
              </Box>
            ) : (selectedTab === "Agents Chats" || selectedTab === "Agents Tickets" || (selectedTab === "Agent CSAT" && csatAgentFilter === 'All Agents')) ? (
              // ✅ UPDATED: Dynamic agent bars (only for visible agents)
              // For counts or stacked %: use sum for height
              (() => {
                const totalForDay = visibleAgentKeys.reduce((sum, key) => sum + (Number(item[key]) || 0), 0);
                const presentAgentKeys = visibleAgentKeys.filter(key => item[key] !== null);
                const hasPresence = presentAgentKeys.length > 0;
                const isCsatAll = selectedTab === "Agent CSAT" && csatAgentFilter === 'All Agents';
                let barHeight;
                if (totalForDay > 0) {
                  barHeight = (totalForDay / yMax) * barAreaHeight;
                } else {
                  if (isCsatAll && hasPresence) {
                    barHeight = 4; // Colored thin for presence with 0s
                  } else {
                    barHeight = 4; // Gray thin otherwise
                  }
                }
                return (
                  <Box
                    onMouseEnter={(e) => handleBarHover(e, item, index)}
                    onMouseLeave={handleBarLeave}
                    sx={{
                      width: `${barWidth}px`,
                      height: `${barHeight}px`,
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    {totalForDay > 0 ? (
                      [...visibleAgentKeys].reverse().map((agentKey) => {
                        const agentName = getAgentDisplayName(agentKey);
                        const agentColor = nameToColor[agentName.toLowerCase()] || "#148DFD";
                        return (
                          <Box
                            key={agentKey}
                            sx={{
                              backgroundColor: agentColor,
                              height: `${((Number(item[agentKey]) || 0) / Math.max(1, totalForDay)) * 100}%`,
                              width: "100%",
                            }}
                          />
                        );
                      })
                    ) : isCsatAll && hasPresence ? (
                      // ✅ NEW: Thin equal segments only for present agents
                      (() => {
                        const numPresent = presentAgentKeys.length;
                        const segmentHeight = barHeight / numPresent;
                        return presentAgentKeys.reverse().map((agentKey) => {
                          const agentName = getAgentDisplayName(agentKey);
                          const agentColor = nameToColor[agentName.toLowerCase()] || "#148DFD";
                          return (
                            <Box
                              key={agentKey}
                              sx={{
                                backgroundColor: agentColor,
                                height: `${segmentHeight}px`,
                                width: "100%",
                              }}
                            />
                          );
                        });
                      })()
                    ) : (
                      <Box sx={getEmptyBarStyle()} />
                    )}
                  </Box>
                );
              })()
            ) : selectedTab === "Agent CSAT" && csatAgentFilter !== "All Agents" ? (
              // ✅ FIXED: Always render bar for single agent (thin colored for 0/absent, full for >0)
              (() => {
                const value = Number(item.value) || 0;
                const hasAgentData = item.hasAgentData || false; // ✅ NEW: Use flag to detect presence (explicit 0 from API)
                // Use passed singleAgentColor or fallback
                const agentColor = singleAgentColor || getAgentColor(csatAgentFilter);
                // ✅ UPDATED: Distinguish heights: full for >0, colored thin 8px if present but 0, NO BAR if absent
                let barHeight;
                let showBar = false;
                if (value > 0) {
                  barHeight = (value / yMax) * barAreaHeight;
                  showBar = true;
                } else if (hasAgentData) {
                  barHeight = 8; // Colored thin for explicit 0
                  showBar = true;
                } else {
                  barHeight = 0; // No bar for absent
                  showBar = false;
                }
                return (
                  <Box
                    onMouseEnter={(e) => handleBarHover(e, item, index)}
                    onMouseLeave={handleBarLeave}
                    sx={{
                      width: `${barWidth}px`,
                      height: `${barHeight}px`,
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    {showBar ? (
                      <Box
                        sx={{
                          height: "100%",  // Full of container (full height or thin 8px)
                          backgroundColor: agentColor,
                          flexShrink: 0,
                          width: "100%",
                        }}
                      />
                    ) : null}  // No visual bar for absent
                  </Box>
                );
              })()
            ) : (
              // ✅ FIXED: Default case (Total Chats, Website Traffic, etc.) - now consistent with stacked: gray thin when no data, blue full when data
              (() => {
                const value = Number(item.value) || 0;
                const barHeight = value <= 0 ? 4 : (value / yMax) * barAreaHeight;  // Thin height for no data
                return (
                  <Box
                    onMouseEnter={(e) => handleBarHover(e, item, index)}
                    onMouseLeave={handleBarLeave}
                    sx={{
                      width: `${barWidth}px`,
                      height: `${barHeight}px`,
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    {value <= 0 ? (
                      <Box sx={getEmptyBarStyle()} />  // Gray thin line
                    ) : (
                      <Box
                        sx={{
                          height: "100%",  // Full of container
                          backgroundColor: "#148DFD",
                          width: "100%",
                        }}
                      />
                    )}
                  </Box>
                );
              })()
            )}
          </Box>
        ))}
      </Box>

      {/* X-axis labels */}
      <Box
        sx={{
          paddingLeft: `${leftPad}px`,
          paddingRight: useFixedCustomBarLayout ? `${leftPad}px` : 0,
          display: "flex",
          justifyContent: useFixedCustomBarLayout ? "center" : "space-between",
          columnGap: useFixedCustomBarLayout ? `${customGap}px` : undefined,
          marginTop: "8px",
        }}
      >
        {xAxisLabels.map((label, index) => {
          // ✅ FIXED: Widen/wrap for ANY Agent CSAT (single OR All) in custom; modest width for non-custom All to fit full labels
          const isAgentCSAT = selectedTab === "Agent CSAT";
          const isAllAgents = csatAgentFilter === "All Agents";
          const isCustomAgentCSAT = useFixedCustomBarLayout && isCustom && isAgentCSAT;
          const labelGroupWidth = isCustomAgentCSAT ? `${groupWidth}px` : isAllAgents ? "50px" : useFixedCustomBarLayout ? `${barWidth}px` : "auto";
          const allowWrap = isCustomAgentCSAT || isAllAgents;  // Wrap/visible for All Agents too
          // ✅ FIXED: Adjust minWidth to avoid overriding alignment in custom mode
          const minWidthValue = allowWrap && !useFixedCustomBarLayout ? "45px" : "auto";
          return (
            <Box
              key={index}
              sx={{
                flex: useFixedCustomBarLayout ? `0 0 ${labelGroupWidth}` : "1",
                width: labelGroupWidth,
                minWidth: minWidthValue === "auto" && !useFixedCustomBarLayout ? 0 : minWidthValue,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#405261",
                  fontFamily: "Inter",
                  textAlign: "center",
                  whiteSpace: allowWrap ? "normal" : "nowrap",  // ✅ Wrap for All Agents (full "13-16")
                  overflow: allowWrap ? "visible" : "hidden",  // ✅ No clip in All
                  lineHeight: 1.2,  // Tighter if multi-line
                  maxWidth: "100%",
                  textOverflow: allowWrap ? "unset" : "ellipsis",  // ✅ No "..." in All
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      {showLegend && legendItems.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "24px",
            marginTop: "16px",
            flexWrap: "wrap",
          }}
        >
          {legendItems.map((item, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  backgroundColor: item.color,
                  borderRadius: "50%",
                }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#405261",
                  fontFamily: "Inter",
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {tooltip && (
        <ChartTooltip
          {...getTooltipData(tooltip.data, tooltip.index)}
          position={tooltip.position}
        />
      )}
    </Box>
  );
};

export default BarChart;